import React from 'react';
import { motion } from 'framer-motion';
interface DigitalClockProps {
  hours: number;
  minutes: number;
  seconds?: number;
  showSeconds?: boolean;
  format?: '12h' | '24h';
  label?: string;
  mode?: 'stimulus' | 'support';
  size?: number;
  className?: string;
}
export function DigitalClock({
  hours,
  minutes,
  seconds = 0,
  showSeconds = false,
  format = '24h',
  label,
  mode = 'support',
  size,
  className = ''
}: DigitalClockProps) {
  // Format the time based on 12h/24h setting
  let displayHours = hours;
  let ampm = '';
  if (format === '12h') {
    ampm = hours >= 12 ? 'PM' : 'AM';
    displayHours = hours % 12;
    if (displayHours === 0) displayHours = 12;
  }
  const hStr = displayHours.toString().padStart(2, '0');
  const mStr = minutes.toString().padStart(2, '0');
  const sStr = seconds.toString().padStart(2, '0');
  // Determine sizing based on mode if not explicitly provided
  const isCompact = mode === 'stimulus';
  const paddingClass = isCompact ? 'p-4' : 'p-6 md:p-8';
  const textClass = isCompact ? 'text-4xl md:text-5xl' : 'text-5xl md:text-7xl';
  const ampmClass = isCompact ? 'text-sm' : 'text-xl';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.9
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className={`flex flex-col items-center gap-3 ${className}`} data-id="element-7613">
      <div className={`relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl md:rounded-3xl border-4 border-slate-700 shadow-2xl overflow-hidden ${paddingClass}`} style={size ? {
      transform: `scale(${size / 100})`,
      transformOrigin: 'center'
    } : undefined} data-id="element-7614">
        {/* Subtle reflection/glare effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" data-id="element-7615" />

        {/* LED Display */}
        <div className="flex items-baseline gap-2 md:gap-3 font-mono font-black text-emerald-400 tracking-wider drop-shadow-[0_0_8px_rgba(52,211,153,0.5)]" data-id="element-7616">
          <div className={textClass} data-id="element-7617">{hStr}</div>
          <div className={`${textClass} pb-1 md:pb-2 animate-pulse`} data-id="element-7618">:</div>
          <div className={textClass} data-id="element-7619">{mStr}</div>

          {showSeconds && <>
              <div className={`${textClass} pb-1 md:pb-2 text-emerald-500/80`} data-id="element-7620">
                :
              </div>
              <div className={`${textClass} text-emerald-500/80`} data-id="element-7621">{sStr}</div>
            </>}

          {format === '12h' && <div className={`${ampmClass} ml-2 font-bold text-emerald-500/80 tracking-normal`} data-id="element-7622">
              {ampm}
            </div>}
        </div>
      </div>

      {label && <p className={`font-semibold text-slate-600 text-center ${isCompact ? 'text-xs' : 'text-sm'}`} data-id="element-7623">
          {label}
        </p>}
    </motion.div>;
}